import { rebuild, buildNodeWithSN, INode, NodeType } from '@xysfe/memento-snapshot';
import * as mittProxy from 'mitt';
import * as smoothscroll from 'smoothscroll-polyfill';
import { Timer } from './timer';
import { createPlayerService } from './machine';
import {
  EventType,
  IncrementalSource,
  fullSnapshotEvent,
  eventWithTime,
  MouseInteractions,
  playerConfig,
  playerMetaData,
  viewportResizeDimention,
  missingNodeMap,
  addedNodeMutation,
  missingNode,
  incrementalSnapshotEvent,
  incrementalData,
  ReplayerEvents,
  Handler,
  Emitter,
  // MediaInteractions,
  metaEvent,
  mutationData,
  scrollData,
  inputData,
  canvasMutationData,
  canvasMutationCommand,
  canvasMutationParam,
  UserDefinedEvent
} from '../types';
import { mirror, polyfill, TreeIndex, dateFormat, getBaseDimension } from '../utils';
import getInjectStyleRules from './styles/inject-style';
import './styles/style.css';

import canvasMutation from './canvas';
import { deserializeArg } from './canvas/deserialize-args';

const SKIP_TIME_THRESHOLD = 10 * 1000;
const SKIP_TIME_INTERVAL = 5 * 1000;
const FORCE_SKIP_TIME_INTERVAL = 5 * 60 * 1000;

// https://github.com/rollup/rollup/issues/1267#issuecomment-296395734
// tslint:disable-next-line
const mitt = (mittProxy as any).default || mittProxy;

const REPLAY_CONSOLE_PREFIX = '[replayer]';

const defaultMouseTailConfig = {
  duration: 500,
  lineCap: 'round',
  lineWidth: 3,
  strokeStyle: 'red',
} as const;

const defaultConfig: playerConfig = {
  normalSpeed: 1,
  speed: 1,
  root: document.body,
  loadTimeout: 0,
  skipInactive: false,
  showWarning: true,
  showDebug: false,
  blockClass: 'mem-block',
  liveMode: false,
  insertStyleRules: [],
  triggerFocus: true,
  UNSAFE_replayCanvas: false,
  mouseTail: defaultMouseTailConfig,
  forceSkipInactive: false,
  mode: 'player'
};

export class Replayer {
  public wrapper: HTMLDivElement;
  public iframe: HTMLIFrameElement;
  public timeDisplayer: HTMLSpanElement;

  public get timer() {
    return this.service.state.context.timer;
  }

  private config: playerConfig;

  private mouse: HTMLDivElement;
  private mouseTail: HTMLCanvasElement | null = null;
  private tailPositions: Array<{ x: number; y: number }> = [];

  private emitter: Emitter = mitt();

  private nextUserInteractionEvent: eventWithTime | null;
  private normalSpeed: number = -1;

  // tslint:disable-next-line: variable-name
  private legacy_missingNodeRetryMap: missingNodeMap = {};

  private service!: ReturnType<typeof createPlayerService>;

  private treeIndex!: TreeIndex;
  private fragmentParentMap!: Map<INode, INode>;

  private unloadStylesheetMap: Record<string, 1> = {};

  private imageMap: Map<eventWithTime | string, HTMLImageElement> = new Map();
  private canvasEventMap: Map<eventWithTime, canvasMutationParam> = new Map();


  constructor(
    events: Array<eventWithTime | string>,
    config?: Partial<playerConfig>,
  ) {
    if (!config?.liveMode && events.length < 2) {
      throw new Error('Replayer need at least 2 events.');
    }
    this.config = Object.assign({}, defaultConfig, config);

    this.handleResize = this.handleResize.bind(this);
    this.getCastFn = this.getCastFn.bind(this);
    this.emitter.on(ReplayerEvents.Resize, this.handleResize as Handler);

    smoothscroll.polyfill();
    polyfill();
    this.setupDom();

    this.treeIndex = new TreeIndex();
    this.fragmentParentMap = new Map<INode, INode>();
    this.emitter.on(ReplayerEvents.Flush, () => {
      const { scrollMap, inputMap } = this.treeIndex.flush();

      for (const d of scrollMap.values()) {
        this.applyScroll(d);
      }
      for (const d of inputMap.values()) {
        this.applyInput(d);
      }

      for (const [frag, parent] of this.fragmentParentMap.entries()) {
        mirror.map[parent.__sn.id] = parent;
        /**
         * If we have already set value attribute on textarea,
         * then we could not apply text content as default value any more.
         */
        if (
          parent.__sn.type === NodeType.Element &&
          parent.__sn.tagName === 'textarea' &&
          frag.textContent
        ) {
          ((parent as unknown) as HTMLTextAreaElement).value = frag.textContent;
        }
        parent.appendChild(frag);
      }
      this.fragmentParentMap.clear();
    });
    this.emitter.on(ReplayerEvents.EventCast, (event: eventWithTime) => {
      if (this.timeDisplayer) {
        this.timeDisplayer.innerText = dateFormat(new Date(event.timestamp), 'yyyy/M/d hh:mm:ss');
      }
    });

    this.service = createPlayerService(
      {
        events: events.map((e) => {
          if (config && config.unpackFn) {
            return config.unpackFn(e as string);
          }
          return e as eventWithTime;
        }),
        timer: new Timer(this.config),
        speed: config?.speed || defaultConfig.speed,
        timeOffset: 0,
        baselineTime: 0,
        lastPlayedEvent: null,
      },
      {
        getCastFn: this.getCastFn,
        emitter: this.emitter,
      },
    );
    this.service.start();
    this.service.subscribe((state) => {
      if (!state.changed) {
        return;
      }
      // publish via emitter
    });

    // rebuild first full snapshot as the poster of the player
    // maybe we can cache it for performance optimization
    const { events: contextEvents } = this.service.state.context;
    const firstMeta = contextEvents.find((e) => e.type === EventType.Meta);
    const firstFullsnapshot = contextEvents.find(
      (e) => e.type === EventType.FullSnapshot,
    );
    if (firstMeta) {
      const { width, height } = firstMeta.data as metaEvent['data'];
      this.emitter.emit(ReplayerEvents.Resize, {
        width,
        height,
      });
    }
    if (firstFullsnapshot) {
      this.rebuildFullSnapshot(
        firstFullsnapshot as fullSnapshotEvent & { timestamp: number },
      );
    }
  }

  public on(event: string, handler: Handler) {
    this.emitter.on(event, handler);
  }

  public setConfig(config: Partial<playerConfig>) {
    Object.keys(config).forEach((key) => {
      // @ts-ignore
      this.config[key] = config[key];
    });
    if (!this.config.skipInactive) {
      this.normalSpeed = -1;
    }
    if (!('normalSpeed' in config) && 'speed' in config) {
      this.config.normalSpeed = config.speed!;
    }
    if (typeof config.mouseTail !== 'undefined') {
      if (config.mouseTail === false) {
        if (this.mouseTail) {
          this.mouseTail.style.display = 'none';
        }
      } else {
        if (!this.mouseTail) {
          this.mouseTail = document.createElement('canvas');
          this.mouseTail.width = Number.parseFloat(this.iframe.width);
          this.mouseTail.height = Number.parseFloat(this.iframe.height);
          this.mouseTail.classList.add('replayer-mouse-tail');
          this.wrapper.insertBefore(this.mouseTail, this.iframe);
        }
        this.mouseTail.style.display = 'inherit';
      }
    }
  }

  public getMetaData(): playerMetaData {
    const { events } = this.service.state.context;
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    return {
      startTime: firstEvent.timestamp,
      endTime: lastEvent.timestamp,
      totalTime: lastEvent.timestamp - firstEvent.timestamp,
    };
  }

  public getCurrentTime(): number {
    return this.timer.timeOffset + this.getTimeOffset();
  }

  public getTimeOffset(): number {
    const { baselineTime, events } = this.service.state.context;
    return baselineTime - events[0].timestamp;
  }

  /**
   * This API was designed to be used as play at any time offset.
   * Since we minimized the data collected from recorder, we do not
   * have the ability of undo an event.
   * So the implementation of play at any time offset will always iterate
   * all of the events, cast event before the offset synchronously
   * and cast event after the offset asynchronously with timer.
   * @param timeOffset number
   */
  public play(timeOffset = 0) {
    this.nextUserInteractionEvent = null;
    this.restoreSpeed();

    if (this.service.state.value === 'ended') {
      this.service.state.context.lastPlayedEvent = null;
      this.service.send({ type: 'REPLAY', payload: { timeOffset } });
    }
    if (this.service.state.value === 'paused') {
      this.service.send({ type: 'RESUME', payload: { timeOffset } });
    } else {
      this.service.send({ type: 'PLAY', payload: { timeOffset } });
    }
    this.emitter.emit(ReplayerEvents.Start);
  }

  public pause() {
    this.service.send({ type: 'PAUSE' });
    this.emitter.emit(ReplayerEvents.Pause);
  }

  public resume(timeOffset = 0) {
    this.service.send({ type: 'RESUME', payload: { timeOffset } });
    this.emitter.emit(ReplayerEvents.Resume);
  }

  public startLive(baselineTime?: number) {
    this.service.send({ type: 'TO_LIVE', payload: { baselineTime } });
  }

  public addEvent(rawEvent: eventWithTime | string) {
    const event = this.config.unpackFn
      ? this.config.unpackFn(rawEvent as string)
      : (rawEvent as eventWithTime);
    Promise.resolve().then(() =>
      this.service.send({ type: 'ADD_EVENT', payload: { event } }),
    );
  }

  public addEvents(rawEvents: Array<eventWithTime | string>) {
    const events = rawEvents.map(e => {
      if (this.config.unpackFn) {
        return this.config.unpackFn(e as string);
      }
      return e as eventWithTime;
    });
    Promise.resolve().then(() =>
      this.service.send({ type: 'ADD_EVENTS', payload: { events } }),
    );
  }

  public enableInteract() {
    this.iframe.setAttribute('scrolling', 'auto');
    this.iframe.style.pointerEvents = 'auto';
  }

  public disableInteract() {
    this.iframe.setAttribute('scrolling', 'no');
    this.iframe.style.pointerEvents = 'none';
  }

  private setupDom() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('replayer-wrapper');
    this.config.root!.appendChild(this.wrapper);

    this.timeDisplayer = document.createElement('span');
    const timeContainer = document.createElement('div');
    timeContainer.classList.add('replayer-time');
    timeContainer.appendChild(this.timeDisplayer);
    this.wrapper.appendChild(timeContainer);

    this.mouse = document.createElement('div');
    this.mouse.classList.add('replayer-mouse');
    this.wrapper.appendChild(this.mouse);

    if (this.config.mouseTail !== false) {
      this.mouseTail = document.createElement('canvas');
      this.mouseTail.classList.add('replayer-mouse-tail');
      this.mouseTail.style.display = 'inherit';
      this.wrapper.appendChild(this.mouseTail);
    }

    this.iframe = document.createElement('iframe');
    const attributes = ['allow-same-origin'];
    if (this.config.UNSAFE_replayCanvas) {
      attributes.push('allow-scripts');
    }
    this.iframe.style.display = 'none';
    this.iframe.setAttribute('sandbox', attributes.join(' '));
    this.disableInteract();
    this.wrapper.appendChild(this.iframe);
  }

  private handleResize(dimension: viewportResizeDimention) {
    this.iframe.style.display = 'inherit';
    for (const el of [this.mouseTail, this.iframe]) {
      if (!el) {
        continue;
      }
      el.setAttribute('width', String(dimension.width));
      el.setAttribute('height', String(dimension.height));
    }
  }

  private getCastFn(event: eventWithTime, isSync = false) {
    let castFn: undefined | (() => void);

    const handleNextActiveSnapshot = () => {
      if (isSync) {
        return;
      }
      const { events } = this.service.state.context;
      let eventIndex = 0
      for (let i = 0, n = events.length; i < n; i++) {
        if (events[i] === event) {
          eventIndex = i
          break;
        }
      }

      if (this.config.forceSkipInactive) {
        const needSkip = events[eventIndex + 1] && (events[eventIndex + 1].timestamp! - event.timestamp! > FORCE_SKIP_TIME_INTERVAL);
        if (needSkip) {
          this.play(events[eventIndex + 1].timestamp! - events[0].timestamp!);
          return;
        }
      }
      
      // 当前帧是下一个有效交互帧
      if (event === this.nextUserInteractionEvent) {
        this.nextUserInteractionEvent = null;
        this.restoreSpeed();
      }
      if (this.config.skipInactive && !this.nextUserInteractionEvent) {
        const { events } = this.service.state.context;
        for (let i = eventIndex + 1, n = events.length; i < n; i++) {
          const _event = events[i];
          if (this.isUserInteraction(_event) || _event.type === EventType.FullSnapshot) {
            if (
              _event.delay! - event.delay! >
              SKIP_TIME_THRESHOLD * this.config.speed
            ) {
              this.nextUserInteractionEvent = _event;
            }
            break;
          }
        }
        if (this.nextUserInteractionEvent) {
          this.normalSpeed = this.config.normalSpeed;
          const skipTime = this.nextUserInteractionEvent.delay! - event.delay!;
          const payload = {
            speed: Math.min(Math.round(skipTime / SKIP_TIME_INTERVAL), 3600),
            normalSpeed: this.normalSpeed,
          };
          this.setConfig(payload);
          this.emitter.emit(ReplayerEvents.SkipStart, payload);
        }
      }
    };

    switch (event.type) {
      case EventType.DomContentLoaded:
      case EventType.Load:
      case EventType.PageChange:
        break;
      case EventType.Custom:
        castFn = () => {
          /**
           * emit custom-event and pass the event object.
           *
           * This will add more value to the custom event and allows the client to react for custom-event.
           */
          this.emitter.emit(ReplayerEvents.CustomEvent, event);
        };
        break;
      case EventType.Meta:
        castFn = () =>
          this.emitter.emit(ReplayerEvents.Resize, {
            width: event.data.width,
            height: event.data.height,
          });
        break;
      case EventType.FullSnapshot:
        castFn = () => {
          this.rebuildFullSnapshot(event);
          this.iframe.contentWindow!.scrollTo(event.data.initialOffset);
          handleNextActiveSnapshot();
        };
        break;
      case EventType.IncrementalSnapshot:
        castFn = () => {
          try {
            this.applyIncremental(event, isSync);
          } catch (error) {
            console.error(error);
          }
          handleNextActiveSnapshot();
        };
        break;
      default:
    }
    const wrappedCastFn = () => {
      const { events } = this.service.state.context;
      if (castFn) {
        castFn();
      }
      this.service.send({ type: 'CAST_EVENT', payload: { event } });
      if (event === events[events.length - 1]) {
        this.restoreSpeed();
        this.service.send('END');
        this.emitter.emit(ReplayerEvents.Finish);
      }
    };
    return wrappedCastFn;
  }

  private rebuildFullSnapshot(
    event: fullSnapshotEvent & { timestamp: number },
  ) {
    if (!this.iframe.contentDocument) {
      return console.warn('Looks like your replayer has been destroyed.');
    }
    if (Object.keys(this.legacy_missingNodeRetryMap).length) {
      console.warn(
        'Found unresolved missing node map',
        this.legacy_missingNodeRetryMap,
      );
    }
    this.legacy_missingNodeRetryMap = {};
    mirror.map = rebuild(event.data.node, this.iframe.contentDocument, true, this.config.cdnHttps)[1];
    const styleEl = document.createElement('style');
    const { documentElement, head } = this.iframe.contentDocument;
    documentElement!.insertBefore(styleEl, head);
    const injectStylesRules = getInjectStyleRules({
      blockClass: this.config.blockClass,
      mode: this.config.mode
    }
    ).concat(this.config.insertStyleRules);
    for (let idx = 0; idx < injectStylesRules.length; idx++) {
      (styleEl.sheet! as CSSStyleSheet).insertRule(injectStylesRules[idx], idx);
    }
    this.emitter.emit(ReplayerEvents.FullsnapshotRebuilded, event);
    if (this.config.mode !== 'page') {
      this.waitForStylesheetLoad();
    }

    
    if (this.config.UNSAFE_replayCanvas) {
      this.preloadAllImages();
    }
  }

  /**
   * pause when loading style sheet, resume when loaded all timeout exceed
   */
  private waitForStylesheetLoad() {
    const head = this.iframe.contentDocument?.head;
    if (head) {
      const unloadSheets: Set<HTMLLinkElement> = new Set();
      let timer: number;
      let beforeLoadState = this.service.state;
      let stylesheets = head.querySelectorAll('link[rel="stylesheet"]');
      stylesheets = Array.prototype.filter.call(stylesheets, (css: HTMLLinkElement) => !!this.unloadStylesheetMap[css.href]);

      stylesheets.forEach((css: HTMLLinkElement) => {
          if (!css.sheet) {
            unloadSheets.add(css);
            this.unloadStylesheetMap[css.href] = 1
            css.addEventListener('load', () => {
              unloadSheets.delete(css);
              // all loaded and timer not released yet
              if (unloadSheets.size === 0 && timer !== -1) {
                if (beforeLoadState.matches('playing')) {
                  this.resume(this.getCurrentTime());
                }
                this.emitter.emit(ReplayerEvents.LoadStylesheetEnd);
                if (timer) {
                  window.clearTimeout(timer);
                }
              }
            });
          }
        });

      if (unloadSheets.size > 0) {
        // find some unload sheets after iterate
        this.service.send({ type: 'PAUSE' });
        this.emitter.emit(ReplayerEvents.LoadStylesheetStart);
        timer = window.setTimeout(() => {
          if (beforeLoadState.matches('playing')) {
            this.resume(this.getCurrentTime());
          }
          // mark timer was called
          timer = -1;
        }, this.config.loadTimeout);
      }
    }
  }

  /**
   * pause when there are some canvas drawImage args need to be loaded
   */
   private async preloadAllImages(): Promise<void[]> {
    let beforeLoadState = this.service.state;
    const stateHandler = () => {
      beforeLoadState = this.service.state;
    };
    this.emitter.on(ReplayerEvents.Start, stateHandler);
    this.emitter.on(ReplayerEvents.Pause, stateHandler);
    const promises: Promise<void>[] = [];
    for (const event of this.service.state.context.events) {
      if (
        event.type === EventType.IncrementalSnapshot &&
        event.data.source === IncrementalSource.CanvasMutation
      ) {
        promises.push(
          this.deserializeAndPreloadCanvasEvents(event.data, event),
        );
        const commands =
          'commands' in event.data ? event.data.commands : [event.data];
        commands.forEach((c) => {
          this.preloadImages(c, event);
        });
      }
    }
    return Promise.all(promises);
  }

  private preloadImages(data: canvasMutationCommand, event: eventWithTime) {
    if (
      data.property === 'drawImage' &&
      typeof data.args[0] === 'string' &&
      !this.imageMap.has(event)
    ) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const imgd = ctx?.createImageData(canvas.width, canvas.height);
      let d = imgd?.data;
      d = JSON.parse(data.args[0]);
      ctx?.putImageData(imgd!, 0, 0);
      
    }
  }
  private async deserializeAndPreloadCanvasEvents(
    data: canvasMutationData,
    event: eventWithTime,
  ) {
    if (!this.canvasEventMap.has(event)) {
      const status = {
        isUnchanged: true,
      };
      if ('commands' in data) {
        const commands = await Promise.all(
          data.commands.map(async (c) => {
            const args = await Promise.all(
              c.args.map(deserializeArg(this.imageMap, null, status)),
            );
            return { ...c, args };
          }),
        );
        if (status.isUnchanged === false)
          this.canvasEventMap.set(event, { ...data, commands });
      } else {
        const args = await Promise.all(
          data.args.map(deserializeArg(this.imageMap, null, status)),
        );
        if (status.isUnchanged === false)
          this.canvasEventMap.set(event, { ...data, args });
      }
    }
  }

  private applyIncremental(
    e: incrementalSnapshotEvent & { timestamp: number },
    isSync: boolean,
  ) {
    const { baselineTime } = this.service.state.context;
    const { data: d } = e;
    switch (d.source) {
      case IncrementalSource.Mutation: {
        if (isSync) {
          d.adds.forEach((m) => this.treeIndex.add(m));
          d.texts.forEach((m) => this.treeIndex.text(m));
          d.attributes.forEach((m) => this.treeIndex.attribute(m));
          d.removes.forEach((m) => this.treeIndex.remove(m));
        }
        this.applyMutation(d, isSync);
        break;
      }
      case IncrementalSource.Drag:
      case IncrementalSource.TouchMove:
      case IncrementalSource.MouseMove:
        if (isSync) {
          const lastPosition = d.positions[d.positions.length - 1];
          this.moveAndHover(d, lastPosition.x, lastPosition.y, lastPosition.id, isSync);
        } else {
          d.positions.forEach((p) => {
            const action = {
              doAction: () => {
                this.moveAndHover(d, p.x, p.y, p.id, isSync);
              },
              delay: p.timeOffset + e.timestamp - baselineTime,
            };
            this.timer.addAction(action);
          });
        }
        break;
      case IncrementalSource.MouseInteraction: {
        /**
         * Same as the situation of missing input target.
         */
        if (d.id === -1) {
          break;
        }
        const event = new Event(MouseInteractions[d.type].toLowerCase());
        const target = mirror.getNode(d.id);
        if (!target) {
          return this.debugNodeNotFound(d, d.id);
        }
        this.emitter.emit(ReplayerEvents.MouseInteraction, {
          type: d.type,
          target,
        });
        const { triggerFocus } = this.config;
        switch (d.type) {
          case MouseInteractions.Blur:
            if ('blur' in ((target as Node) as HTMLElement)) {
              ((target as Node) as HTMLElement).blur();
            }
            break;
          case MouseInteractions.Focus:
            if (triggerFocus && ((target as Node) as HTMLElement).focus) {
              ((target as Node) as HTMLElement).focus({
                preventScroll: true,
              });
            }
            break;
          case MouseInteractions.Click:
          case MouseInteractions.TouchStart:
          case MouseInteractions.TouchEnd:
            /**
             * Click has no visual impact when replaying and may
             * trigger navigation when apply to an <a> link.
             * So we will not call click(), instead we add an
             * animation to the mouse element which indicate user
             * clicked at this moment.
             */
            if (!isSync) {
              this.moveAndHover(d, d.x, d.y, d.id, isSync);
              this.mouse.classList.remove('active');
              // tslint:disable-next-line
              void this.mouse.offsetWidth;
              this.mouse.classList.add('active');
            }
            break;
          default:
            target.dispatchEvent(event);
        }
        break;
      }
      case IncrementalSource.Scroll: {
        /**
         * Same as the situation of missing input target.
         */
        if (d.id === -1) {
          break;
        }
        if (isSync) {
          this.treeIndex.scroll(d);
          break;
        }
        this.applyScroll(d);
        break;
      }
      case IncrementalSource.ViewportResize:
        // 20200819 koma 非屏幕旋转的resize不做处理, 主要因为android键盘弹起时会触发resize
        if (d.rotate) {
          this.emitter.emit(ReplayerEvents.Resize, {
            width: d.width,
            height: d.height,
          });
        }
        break;
      case IncrementalSource.Input: {
        /**
         * Input event on an unserialized node usually means the event
         * was synchrony triggered programmatically after the node was
         * created. This means there was not an user observable interaction
         * and we do not need to replay it.
         */
        if (d.id === -1) {
          break;
        }
        if (isSync) {
          this.treeIndex.input(d);
          break;
        }
        this.applyInput(d);
        break;
      }
      // case IncrementalSource.MediaInteraction: {
      //   const target = mirror.getNode(d.id);
      //   if (!target) {
      //     return this.debugNodeNotFound(d, d.id);
      //   }
      //   const mediaEl = (target as Node) as HTMLMediaElement;
      //   if (d.type === MediaInteractions.Pause) {
      //     mediaEl.pause();
      //   }
      //   if (d.type === MediaInteractions.Play) {
      //     if (mediaEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      //       mediaEl.play();
      //     } else {
      //       mediaEl.addEventListener('canplay', () => {
      //         mediaEl.play();
      //       });
      //     }
      //   }
      //   break;
      // }
      case IncrementalSource.StyleSheetRule: {
        const target = mirror.getNode(d.id);
        if (!target) {
          return this.debugNodeNotFound(d, d.id);
        }

        const styleEl = (target as Node) as HTMLStyleElement;
        const styleSheet = <CSSStyleSheet>styleEl.sheet;

        if (d.adds) {
          d.adds.forEach(({ rule, index }) => {
            const _index =
              index === undefined
                ? undefined
                : Math.min(index, styleSheet.rules.length);
            try {
              styleSheet.insertRule(rule, _index);
            } catch (e) {
              /**
               * sometimes we may capture rules with browser prefix
               * insert rule with prefixs in other browsers may cause Error
               */
            }
          });
        }

        if (d.removes) {
          d.removes.forEach(({ index }) => {
            styleSheet.deleteRule(index);
          });
        }
        break;
      }

      case IncrementalSource.CanvasMutation: {
        if (!this.config.UNSAFE_replayCanvas) {
          return;
        }
        const target = mirror.getNode(d.id);
        if (!target) {
          return this.debugNodeNotFound(d, d.id);
        }

        canvasMutation({
          event: e,
          mutation: d,
          target: target as unknown as HTMLCanvasElement,
          imageMap: this.imageMap,
          canvasEventMap: this.canvasEventMap,
          errorHandler: this.warnCanvasMutationFailed.bind(this),
        });

        break;
      }
      case IncrementalSource.UserDefinedEvent: {
        if (d.evt === UserDefinedEvent.LivePlay) {
          const document = this.iframe.contentDocument;
          if (!document || !d.selector || !d.url) {
            return;
          }

          const element = document.querySelector(d.selector);
          if (element && element.tagName && element.tagName.toLowerCase() === 'video') {
            element.setAttribute('src', d.url);
            (element as HTMLVideoElement).play();
            const customEvent = new CustomEvent('memento-player-live-play', {
              detail: {
                target: element
              },
              bubbles: true,
              cancelable: true
            });
            window.document.dispatchEvent(customEvent);
          }
        }
        break;
      }
      default:
    }
  }

  private applyMutation(d: mutationData, useVirtualParent: boolean) {
    d.removes.forEach((mutation) => {
      const target = mirror.getNode(mutation.id);
      if (!target) {
        return this.warnNodeNotFound(d, mutation.id);
      }
      const parent = mirror.getNode(mutation.parentId);
      if (!parent) {
        return this.warnNodeNotFound(d, mutation.parentId);
      }
      // target may be removed with its parents before
      mirror.removeNodeFromMap(target);
      if (parent) {
        const realParent = this.fragmentParentMap.get(parent) || parent;
        const realTarget = this.fragmentParentMap.get(target) || target;
        if (realParent.contains(realTarget)) {
          realParent.removeChild(realTarget);
        } else {
          console.warn(`=====target: ${mutation.id} dont exist in parent: ${mutation.parentId}======, `)
        }
      }
    });

    const legacy_missingNodeMap: missingNodeMap = {
      ...this.legacy_missingNodeRetryMap,
    };
    const queue: addedNodeMutation[] = [];

    const appendNode = (mutation: addedNodeMutation) => {
      if (!this.iframe.contentDocument) {
        return console.warn('Looks like your replayer has been destroyed.');
      }
      let parent = mirror.getNode(mutation.parentId);
      if (!parent) {
        return queue.push(mutation);
      }

      const parentInDocument = this.iframe.contentDocument.contains(parent);
      if (useVirtualParent && parentInDocument) {
        const virtualParent = (document.createDocumentFragment() as unknown) as INode;
        mirror.map[mutation.parentId] = virtualParent;
        this.fragmentParentMap.set(virtualParent, parent);
        while (parent.firstChild) {
          virtualParent.appendChild(parent.firstChild);
        }
        parent = virtualParent;
      }

      let previous: Node | null = null;
      let next: Node | null = null;
      if (mutation.previousId) {
        previous = mirror.getNode(mutation.previousId) as Node;
      }
      if (mutation.nextId) {
        next = mirror.getNode(mutation.nextId) as Node;
      }
      // next not present at this moment
      if (mutation.nextId !== null && mutation.nextId !== -1 && !next) {
        return queue.push(mutation);
      }

      // domId 已存在则不添加， 防止重复的问题， 新续保v3回溯问题都修复
      if (mirror.has(mutation.node.id)) {
        return
      }

      const target = buildNodeWithSN(
        mutation.node,
        this.iframe.contentDocument,
        mirror.map,
        true,
        true,
        this.config.cdnHttps,
      ) as Node;

      // legacy data, we should not have -1 siblings any more
      if (mutation.previousId === -1 || mutation.nextId === -1) {
        legacy_missingNodeMap[mutation.node.id] = {
          node: target,
          mutation,
        };
        return;
      }

      if (previous && previous.nextSibling && previous.nextSibling.parentNode) {
        parent.insertBefore(target, previous.nextSibling);
      } else if (next && next.parentNode) {
        // making sure the parent contains the reference nodes
        // before we insert target before next.
        parent.contains(next)
          ? parent.insertBefore(target, next)
          : parent.insertBefore(target, null);
      } else {
        parent.appendChild(target);
      }

      if (mutation.previousId || mutation.nextId) {
        this.legacy_resolveMissingNode(
          legacy_missingNodeMap,
          parent,
          target,
          mutation,
        );
      }
    };

    d.adds.forEach((mutation) => {
      appendNode(mutation);
    });

    while (queue.length) {
      if (queue.every((m) => !Boolean(mirror.getNode(m.parentId)))) {
        return queue.forEach((m) => this.warnNodeNotFound(d, m.node.id));
      }
      const mutation = queue.shift()!;
      appendNode(mutation);
    }

    if (Object.keys(legacy_missingNodeMap).length) {
      Object.assign(this.legacy_missingNodeRetryMap, legacy_missingNodeMap);
    }

    d.texts.forEach((mutation) => {
      let target = mirror.getNode(mutation.id);
      if (!target) {
        return this.warnNodeNotFound(d, mutation.id);
      }
      /**
       * apply text content to real parent directly
       */
      if (this.fragmentParentMap.has(target)) {
        target = this.fragmentParentMap.get(target)!;
      }
      target.textContent = mutation.value;
    });
    d.attributes.forEach((mutation) => {
      let target = mirror.getNode(mutation.id);
      if (!target) {
        return this.warnNodeNotFound(d, mutation.id);
      }
      if (this.fragmentParentMap.has(target)) {
        target = this.fragmentParentMap.get(target)!;
      }
      for (let attributeName in mutation.attributes) {
        if (typeof attributeName === 'string') {
          // 如果有镜像属性，则忽略当前属性
          if (mutation.attributes.hasOwnProperty(`_memimage_${attributeName}`)) {
            continue;
          }
          const value = mutation.attributes[attributeName];
          // 处理镜像属性
          attributeName = attributeName.replace('_memimage_', '');
          if (value !== null) {
            if (((target as Node) as Element).tagName === 'video' && attributeName === 'mem_placeholder') {
              ((target as Node) as HTMLVideoElement).style.background = `center / cover no-repeat url(${value})`;
            } else {
              ((target as Node) as Element).setAttribute(attributeName, value);
            }
          } else {
            ((target as Node) as Element).removeAttribute(attributeName);
          }
        }
      }
    });
  }

  private applyScroll(d: scrollData) {
    const target = mirror.getNode(d.id);
    if (!target) {
      return this.debugNodeNotFound(d, d.id);
    }
    if ((target as Node) === this.iframe.contentDocument) {
      this.iframe.contentWindow!.scrollTo({
        top: d.y,
        left: d.x,
        behavior: 'smooth',
      });
    } else {
      try {
        ((target as Node) as Element).scrollTop = d.y;
        ((target as Node) as Element).scrollLeft = d.x;
      } catch (error) {
        /**
         * Seldomly we may found scroll target was removed before
         * its last scroll event.
         */
      }
    }
  }

  private applyInput(d: inputData) {
    const target = mirror.getNode(d.id);
    if (!target) {
      return this.debugNodeNotFound(d, d.id);
    }
    try {
      ((target as Node) as HTMLInputElement).checked = d.isChecked;
      ((target as Node) as HTMLInputElement).value = d.text;
    } catch (error) {
      // for safe
    }
  }

  private legacy_resolveMissingNode(
    map: missingNodeMap,
    parent: Node,
    target: Node,
    targetMutation: addedNodeMutation,
  ) {
    const { previousId, nextId } = targetMutation;
    const previousInMap = previousId && map[previousId];
    const nextInMap = nextId && map[nextId];
    if (previousInMap) {
      const { node, mutation } = previousInMap as missingNode;
      parent.insertBefore(node, target);
      delete map[mutation.node.id];
      delete this.legacy_missingNodeRetryMap[mutation.node.id];
      if (mutation.previousId || mutation.nextId) {
        this.legacy_resolveMissingNode(map, parent, node as Node, mutation);
      }
    }
    if (nextInMap) {
      const { node, mutation } = nextInMap as missingNode;
      parent.insertBefore(node, target.nextSibling);
      delete map[mutation.node.id];
      delete this.legacy_missingNodeRetryMap[mutation.node.id];
      if (mutation.previousId || mutation.nextId) {
        this.legacy_resolveMissingNode(map, parent, node as Node, mutation);
      }
    }
  }

  private moveAndHover(d: incrementalData, x: number, y: number, id: number, isSync: boolean) {
    this.mouse.style.left = `${x}px`;
    this.mouse.style.top = `${y}px`;
    const target = mirror.getNode(id);
    if (!target) {
      return this.debugNodeNotFound(d, id);
    }

    const base = getBaseDimension(target, this.iframe);
    const _x = x * base.absoluteScale + base.x;
    const _y = y * base.absoluteScale + base.y;

    this.mouse.style.left = `${_x}px`;
    this.mouse.style.top = `${_y}px`;

    if (!isSync) {
      this.drawMouseTail({ x: _x, y: _y });
    }
    this.hoverElements((target as Node) as Element);
  }

  private drawMouseTail(position: { x: number; y: number }) {
    if (!this.mouseTail) {
      return;
    }

    const { lineCap, lineWidth, strokeStyle, duration } =
      this.config.mouseTail === true
        ? defaultMouseTailConfig
        : Object.assign({}, defaultMouseTailConfig, this.config.mouseTail);

    const draw = () => {
      if (!this.mouseTail) {
        return;
      }
      const ctx = this.mouseTail.getContext('2d');
      if (!ctx || !this.tailPositions.length) {
        return;
      }
      ctx.clearRect(0, 0, this.mouseTail.width, this.mouseTail.height);
      ctx.beginPath();
      ctx.lineWidth = lineWidth;
      ctx.lineCap = lineCap;
      ctx.strokeStyle = strokeStyle;
      ctx.moveTo(this.tailPositions[0].x, this.tailPositions[0].y);
      this.tailPositions.forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    };

    this.tailPositions.push(position);
    draw();
    setTimeout(() => {
      this.tailPositions = this.tailPositions.filter((p) => p !== position);
      draw();
    }, duration / this.config.speed);
    
  }

  private hoverElements(el: Element) {
    this.iframe.contentDocument
      ?.querySelectorAll('.\\:hover')
      .forEach((hoveredEl) => {
        hoveredEl.classList.remove(':hover');
      });
    let currentEl: Element | null = el;
    while (currentEl) {
      if (currentEl.classList) {
        currentEl.classList.add(':hover');
      }
      currentEl = currentEl.parentElement;
    }
  }

  private isUserInteraction(event: eventWithTime): boolean {
    if (event.type !== EventType.IncrementalSnapshot) {
      return false;
    }
    return (
      event.data.source > IncrementalSource.Mutation &&
      event.data.source <= IncrementalSource.Input
    );
  }

  private restoreSpeed() {
    if (this.normalSpeed === -1) {
      return;
    }
    const payload = { speed: this.normalSpeed };
    this.setConfig(payload);
    this.emitter.emit(ReplayerEvents.SkipEnd, payload);
    this.normalSpeed = -1;
  }

  private warnNodeNotFound(d: incrementalData, id: number) {
    if (!this.config.showWarning) {
      return;
    }
    console.warn(REPLAY_CONSOLE_PREFIX, `Node with id '${id}' not found in`, d);
  }

  private warnCanvasMutationFailed(
    d: canvasMutationData | canvasMutationCommand,
    error: unknown,
  ) {
    console.warn(`Has error on canvas update`, error, 'canvas mutation:', d);
  }

  private debugNodeNotFound(d: incrementalData, id: number) {
    /**
     * There maybe some valid scenes of node not being found.
     * Because DOM events are macrotask and MutationObserver callback
     * is microtask, so events fired on a removed DOM may emit
     * snapshots in the reverse order.
     */
    if (!this.config.showDebug) {
      return;
    }
    // tslint:disable-next-line: no-console
    console.log(REPLAY_CONSOLE_PREFIX, `Node with id '${id}' not found in`, d);
  }
}
